import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import { RequestLog } from '@engine/models'

import { Table } from '#components/Table'

import { QuoteStatusBadge } from './QuoteStatusBadge.jsx'

interface QuoteListProps {
  quotes: RequestLog[]
  selectedId?: string
  onSelect: (quote: RequestLog) => void
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
}

const columnHelper = createColumnHelper<RequestLog>()

const QuoteList = ({
  quotes,
  selectedId,
  onSelect,
  infiniteScrollRef,
  isFetchingMore,
}: QuoteListProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('externalId', {
        header: 'External ID',
        cell: info => (
          <div className="truncate max-w-[200px]">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('requestId', {
        header: 'Request ID',
        cell: info => <div className="text-xs">{info.getValue()}</div>,
      }),
      columnHelper.accessor('carrierCode', {
        header: 'Carriers',
        cell: info => {
          const carriers = info.getValue()
          return (
            <div className="flex flex-wrap gap-1">
              {carriers?.slice(0, 3).map((carrier, idx) => (
                <span key={idx} className="badge badge-sm badge-info uppercase">
                  {carrier}
                </span>
              ))}
              {carriers?.length > 3 && (
                <span className="badge badge-sm badge-ghost">
                  +{carriers.length - 3} more
                </span>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor('tenantRequest.content.serviceTypes', {
        header: 'Service Types',
        cell: info => info.getValue()?.join(),
      }),
      columnHelper.accessor('tenantResponse.content', {
        header: 'Status',
        cell: info => <QuoteStatusBadge log={info.row.original} />,
      }),
      columnHelper.accessor('tenantRequest.requestDate', {
        header: 'Request Date',
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : null
        },
      }),
      columnHelper.accessor('tenantResponse.responseTime', {
        header: 'Response Time',
        cell: info => {
          const time = info.getValue()
          if (!time) return <span className="text-xs opacity-50">N/A</span>
          const formatted =
            time < 1000 ? `${time}ms` : `${(time / 1000).toFixed(2)}s`
          const colorClass =
            time < 1000
              ? 'text-success'
              : time < 3000
                ? 'text-warning'
                : 'text-error'
          return (
            <span className={`text-xs font-semibold ${colorClass}`}>
              {formatted}
            </span>
          )
        },
      }),
    ],
    [],
  )

  return (
    <Table
      data={quotes}
      columns={columns}
      getRowId={quote => quote._id}
      selectedId={selectedId}
      onRowClick={onSelect}
      infiniteScrollRef={infiniteScrollRef}
      isFetchingMore={isFetchingMore}
    />
  )
}

export { QuoteList }
